import { EventEmitter } from 'events'
import * as Sentry from '@sentry/browser'
import { invalidAuth } from 'reduxModules/auth/'
import authTypes from 'reduxModules/auth/types'
import { types as confirmationTypes } from 'reduxModules/confirmation'
import { reloadAsLoggedOut } from '../utils/session'
import { toastServerError } from '../global/notifications'
import { normalizeErrors } from './modules/utils/forms'

// Custom error class to replace redux-form's SubmissionError
export class FormSubmissionError extends Error {
  constructor(errors) {
    super('Form submission error')
    this.name = 'FormSubmissionError'
    this.errors = errors
  }
}

export const sentryMiddleware = () => (next) => (action) => {
  // Prevent from catching forms errors
  if (action.payload && action.payload.status === 400) return next(action)
  try {
    return next(action)
  } catch (err) {
    Sentry.captureException(err, {
      extra: { action: action.type }
    })
    throw err
  }
}

export const fetchFailureMiddleware = () => (next) => (action) => {
  if (action.payload && action.payload.status === 400) {
    const response = action.payload.value
    const errors = normalizeErrors(response)
    next({ ...action, payload: response })
    throw new FormSubmissionError(errors)
  }
  return next(action)
}

// Tokens issued before the backend's JWT migration are rejected with this
// exact simplejwt error on every request. Only the /user fetch failure is
// trusted as the logout signal so other 401s keep their existing handling.
const isStaleTokenUserFailure = (action) =>
  action.type === authTypes.AUTH_FETCH_USER_FAILURE &&
  action.payload &&
  action.payload.status === 401 &&
  action.payload.value &&
  action.payload.value.code === 'token_not_valid' &&
  action.payload.value.detail === 'Given token not valid for any token type'

const LEGACY_EXPIRED_DETAILS = [
  'Signature has expired.',
  'Error decoding signature.'
]

// SPEC-095: the 401s that mean "this login is over", and why. The backend
// writes the single-session detail across a line continuation, so details
// are compared with whitespace collapsed. A 403 never ends a session: it
// means "not allowed" (e.g. the card-add gate) and is left to the caller.
export const sessionEndReason = (action) => {
  const payload = action && action.payload
  if (!payload || payload.status !== 401 || !payload.value) return null
  const detail = String(payload.value.detail || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (detail.startsWith('A session is already active in another device'))
    return 'other_device'
  if (LEGACY_EXPIRED_DETAILS.includes(detail)) return 'expired'
  if (isStaleTokenUserFailure(action)) return 'expired'
  return null
}

// A 403 means "not allowed", never "logged out": the backend's own words
// (e.g. the card-add gate) are shown, and the visitor stays where they are.
const forbiddenDetail = (action) => {
  const payload = action && action.payload
  if (!payload || payload.status !== 403) return null
  const detail = payload.value && payload.value.detail
  return typeof detail === 'string' && detail.trim()
    ? detail
    : "You don't have access to this."
}

// Several requests fail with the same dead token; log out and reload once.
let sessionEnding = false
export const resetSessionEnding = () => {
  sessionEnding = false
}

export const authFailureMiddleware = (store) => (next) => (action) => {
  const forbidden = forbiddenDetail(action)
  if (forbidden) toastServerError(forbidden)
  const reason = sessionEndReason(action)
  if (reason && !sessionEnding) {
    sessionEnding = true
    // reload first: it records the current page before the logout below
    // lets RequireAuth move a guarded page to /login
    reloadAsLoggedOut(reason)
    store.dispatch(invalidAuth())
  }
  return next(action)
}

export const multiEffectsMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (Array.isArray(action))
      return Promise.all(action.filter(Boolean).map(dispatch))
    return next(action)
  }

let pending

// eslint-disable-next-line no-unused-vars
export const confirmationMiddleware = (store) => (next) => (action) => {
  switch (action.type) {
    case confirmationTypes.INIT_CONFIRMATION:
      next(action)
      pending = new EventEmitter()
      return pending

    case confirmationTypes.RESOLVE_CONFIRMATION:
      pending.emit(action.payload.resolution, action.payload.feedback)
      pending = null
      // falls through
    default:
      return next(action)
  }
}

export default {
  authFailureMiddleware,
  fetchFailureMiddleware,
  multiEffectsMiddleware,
  confirmationMiddleware
}
